import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Container,
  Divider,
  Fade,
  Zoom,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import Logo from "../components/Logo";
import "../styles/global.css";

// ===== ADD THE FLOAT ANIMATION =====
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const Login = () => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    setTimeout(() => {
      const success = login(password);
      setLoading(false);

      if (success) {
        navigate("/dashboard");
      } else {
        setError("Invalid access code. Please try again.");
        setPassword("");
      }
    }, 800);
  };

  // Handle 300 Mighty Men application click
  const handleMightyMenApply = () => {
    // Navigate to a dedicated application page
    navigate("/mighty-men-apply");
    
    // OR if you want to open in a new tab:
    // window.open("/mighty-men-apply", "_blank");
    
    // OR if you want to go to an external URL:
    // window.location.href = "https://your-domain.com/mighty-men-apply";
  };

  // Sector items - Finance first
  const sectors = [
    "Finance",
    "Energy",
    "Precious Metals",
    "Communications",
    "Agriculture",
    "Legal",
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
        background: "#0a0e1a",
        "&::before": {
          content: '""',
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(/mightymenofdavid.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.55,
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(160deg, rgba(10, 14, 26, 0.75) 0%, rgba(20, 25, 50, 0.7) 50%, rgba(36, 51, 138, 0.6) 100%)",
          zIndex: 0,
        },
      }}
    >
      {/* Top section with sectors - Clean Design */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          py: 2.5,
          px: { xs: 2, md: 4 },
          background: "rgba(10, 14, 26, 0.8)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 4px 30px rgba(0,0,0,0.3)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Sector Pills - Centered */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.2)",
              fontSize: "0.45rem",
              letterSpacing: "3px",
              textTransform: "uppercase",
              fontWeight: "400",
              mr: 0.5,
            }}
          >
            Worldwide Corporation
          </Typography>
          
          <Box
            sx={{
              width: 1,
              height: 16,
              background: "rgba(255,255,255,0.05)",
              mx: 0.5,
            }}
          />
          
          {sectors.map((sector, index) => (
            <Fade in timeout={300 + index * 80} key={sector}>
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2.5 },
                  py: 0.6,
                  borderRadius: "50px",
                  border: index === 0 
                    ? "1px solid rgba(74, 144, 217, 0.4)" 
                    : "1px solid rgba(255,255,255,0.06)",
                  background: index === 0 
                    ? "linear-gradient(135deg, rgba(74, 144, 217, 0.2), rgba(165, 164, 206, 0.1))"
                    : "rgba(255,255,255,0.03)",
                  backdropFilter: "blur(5px)",
                  color: index === 0 
                    ? "#4a90d9" 
                    : "rgba(255,255,255,0.5)",
                  fontSize: { xs: "0.45rem", sm: "0.55rem" },
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  fontWeight: index === 0 ? "700" : "500",
                  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  cursor: "default",
                  whiteSpace: "nowrap",
                  boxShadow: index === 0 
                    ? "0 0 20px rgba(74, 144, 217, 0.1)" 
                    : "none",
                  "&:hover": {
                    background: "rgba(255,255,255,0.08)",
                    borderColor: "rgba(74, 144, 217, 0.2)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 25px rgba(0,0,0,0.2)",
                    color: "#4a90d9",
                  },
                }}
              >
                {sector}
              </Box>
            </Fade>
          ))}
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, mt: 10 }}>
        <Grid container spacing={8} alignItems="center">
          {/* Left Side - Company Info */}
          <Grid item xs={12} md={7}>
            <Fade in timeout={800}>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    left: -40,
                    width: 200,
                    height: 200,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.02)",
                    pointerEvents: "none",
                  }}
                />
                
                <Typography
                  variant="overline"
                  sx={{
                    color: "#a5a4ce",
                    letterSpacing: "10px",
                    fontSize: "0.65rem",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    mb: 1.5,
                    display: "block",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      left: 0,
                      width: 30,
                      height: 2,
                      background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                      opacity: 0.5,
                    },
                  }}
                >
                  Since 1995
                </Typography>
                
                {/* Logo - Twice the size */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                    transform: "scale(2)",
                    transformOrigin: "left center",
                  }}
                >
                  <Logo size="large" variant="icon" />
                </Box>
                
                <Box
                  sx={{
                    width: 80,
                    height: 3.5,
                    background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                    mb: 3.5,
                    borderRadius: 2,
                  }}
                />
                
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontFamily: "'Georgia', serif",
                    fontWeight: "300",
                    mb: 2,
                    letterSpacing: "0.3px",
                    lineHeight: 1.9,
                    maxWidth: "90%",
                  }}
                >
                  A premier multinational conglomerate with a legacy of excellence 
                  spanning over three decades. We deliver innovative solutions across 
                  finance, energy, precious metals, agriculture, communications, and legal sectors.
                </Typography>

                <Box
                  sx={{
                    position: "relative",
                    mb: 4,
                    maxWidth: "85%",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      background: "linear-gradient(180deg, #4a90d9, #a5a4ce)",
                      borderRadius: 2,
                      opacity: 0.5,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      fontFamily: "'Georgia', serif",
                      letterSpacing: "0.5px",
                      lineHeight: 1.9,
                      fontStyle: "italic",
                      pl: 3.5,
                      fontSize: "0.95rem",
                    }}
                  >
                    "Driving global progress through strategic diversification, 
                    innovation, and unwavering commitment to excellence."
                  </Typography>
                </Box>
                
               
                {/* Decorative line at bottom */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 1,
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05))",
                    }}
                  />
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: "rgba(74, 144, 217, 0.3)",
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      height: 1,
                      background: "linear-gradient(90deg, rgba(255,255,255,0.05), transparent)",
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={5}>
            <Zoom in timeout={600}>
              <Paper
                elevation={0}
                sx={{
                  padding: { xs: 3.5, sm: 4.5 },
                  borderRadius: 4.5,
                  background: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(30px)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  position: "relative",
                  zIndex: 1,
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: "linear-gradient(90deg, #24338a, #4a90d9, #a5a4ce)",
                    opacity: 0.8,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",                                                     
                    top: -50,
                    right: -50,
                    width: 100,
                    height: 100,
                    background: "radial-gradient(circle, rgba(165,164,206,0.03), transparent)",
                    borderRadius: "50%",
                    pointerEvents: "none",
                  },
                  "&:hover": {
                    boxShadow: "0 25px 70px rgba(0,0,0,0.5)",
                    borderColor: "rgba(255,255,255,0.1)",
                  },
                  transition: "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#ffffff",
                      mt: 0,
                      mb: 0.5,
                      fontFamily: "'Georgia', serif",
                      fontWeight: "bold",
                      letterSpacing: "5px",
                      textTransform: "uppercase",
                      fontSize: "1.1rem",
                    }}
                  >
                    Executive Access
                  </Typography>
                  
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.35)",
                      fontSize: "0.6rem",
                      letterSpacing: "6px",
                      textTransform: "uppercase",
                      mb: 3,
                      fontWeight: "500",
                    }}
                  >
                    Private & Confidential
                  </Typography>

                  <Divider
                    sx={{
                      width: 50,
                      mb: 3.5,
                      borderColor: "rgba(255,255,255,0.1)",
                      borderWidth: 2,
                    }}
                  />

                  <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Access Code"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      sx={{
                        mb: 2.5,
                        "& .MuiOutlinedInput-root": {
                          color: "#ffffff",
                          borderRadius: 2.5,
                          background: "rgba(255,255,255,0.05)",
                          transition: "all 0.3s ease",
                          "& fieldset": {
                            borderColor: "rgba(255,255,255,0.1)",
                            borderWidth: 1.5,
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(74, 144, 217, 0.5)",
                            borderWidth: 2,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#4a90d9",
                            borderWidth: 2,
                            boxShadow: "0 0 0 4px rgba(74, 144, 217, 0.1)",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.4)",
                          fontWeight: "500",
                          "&.Mui-focused": {
                            color: "#4a90d9",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ color: "rgba(255,255,255,0.25)", mr: 1.5, fontSize: 18 }}>
                            🔒
                          </Typography>
                        ),
                      }}
                    />

                    {error && (
                      <Fade in>
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mb: 2.5, 
                            borderRadius: 2.5,
                            background: "rgba(255,0,0,0.05)",
                            border: "1px solid rgba(255,0,0,0.1)",
                            color: "#ff6b6b",
                            "& .MuiAlert-icon": {
                              color: "#ff6b6b",
                            },
                          }}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={loading || !password}
                      sx={{
                        mt: 1,
                        py: 1.75,
                        borderRadius: 2.5,
                        background: "linear-gradient(135deg, #24338a, #4a90d9)",
                        color: "#ffffff",
                        fontFamily: "'Georgia', serif",
                        letterSpacing: "5px",
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
                          transition: "all 0.6s ease",
                        },
                        "&:hover": {
                          background: "linear-gradient(135deg, #1a2a70, #3a7abf)",
                          boxShadow: "0 8px 35px rgba(74, 144, 217, 0.3)",
                          transform: "translateY(-2px)",
                          "&::before": {
                            left: "100%",
                          },
                        },
                        "&:disabled": {
                          background: "rgba(255,255,255,0.05)",
                          color: "rgba(255,255,255,0.3)",
                          transform: "none",
                        },
                        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                      }}
                    >
                      {loading ? "VERIFYING..." : "Access Portal"}
                    </Button>
                  </form>

                  {/* Decorative corner elements */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 20,
                      right: 20,
                      width: 60,
                      height: 60,
                      border: "1px solid rgba(255,255,255,0.02)",
                      borderRadius: "50%",
                      pointerEvents: "none",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 35,
                      right: 35,
                      width: 30,
                      height: 30,
                      border: "1px solid rgba(255,255,255,0.01)",
                      borderRadius: "50%",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* ===== 300 MIGHTY MEN FLOATING CALLOUT - UPDATED ===== */}
      <Box
        sx={{
          position: "fixed",
          bottom: 30,
          left: 30,
          zIndex: 999,
          p: 1.5,
          pr: 2.5,
          borderRadius: 4,
          background: "rgba(255,215,0,0.03)",
          border: "1px solid rgba(255,215,0,0.06)",
          backdropFilter: "blur(20px)",
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 1.5,
          transition: "all 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
          animation: `${float} 4s ease-in-out infinite`,
          cursor: "pointer",
          maxWidth: "180px",
          "&:hover": {
            background: "rgba(255,215,0,0.06)",
            borderColor: "rgba(255,215,0,0.12)",
            transform: "translateY(-6px) scale(1.02)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          },
        }}
        onClick={handleMightyMenApply}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.2rem",
            border: "1px solid rgba(255,255,255,0.06)",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "0.9rem",
              fontWeight: "bold",
              fontFamily: "'Georgia', serif",
            }}
          >
            300
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              color: "#ffd700",
              fontSize: "0.6rem",
              fontWeight: "bold",
              letterSpacing: "0.3px",
              lineHeight: 1.2,
            }}
          >
            Recruiting the
            <br />
            300-Mighty Men
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.25)",
              fontSize: "0.45rem",
              letterSpacing: "0.5px",
              mt: 0.25,
            }}
          >
            Click to Apply →
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;